import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  GripVertical,
  ChevronUp,
  ChevronDown,
  Trash2,
  Copy,
} from 'lucide-react';
import type { TemplateItem } from '../../../../src/schemas/templateContent';

interface SortableItemProps {
  item: TemplateItem;
  index: number;
  totalItems: number;
  isActive: boolean;
  depth?: number;
  onSelect: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onAddSubAnswer: (parentId: string) => void;
  onToggleExpanded?: (id: string) => void;
  isExpanded?: boolean;
}

function SortableItem({
  item,
  index,
  totalItems,
  isActive,
  depth = 0,
  onSelect,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onAddSubAnswer,
  onToggleExpanded,
  isExpanded = true,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasChildren = item.children && item.children.length > 0;
  const canMoveUp = index > 0;
  const canMoveDown = index < totalItems - 1;

  return (
    <div ref={setNodeRef} style={style} className="mb-2">
      <Card
        className={`cursor-pointer transition-all hover:shadow-md ${
          isActive ? 'ring-2 ring-primary' : ''
        }`}
        style={{ marginLeft: depth * 24 }}
        onClick={() => onSelect(item.id)}
        data-testid={`item-${item.id}`}
      >
        <div className="flex items-center gap-2 p-3">
          {/* Drag Handle */}
          <button
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-5 h-5" />
          </button>


          {/* Item Label and Type */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">{item.label}</span>
              <Badge variant="outline" className="text-xs">
                {item.type}
              </Badge>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMoveUp(item.id)}
              disabled={!canMoveUp}
              className="h-8 w-8 p-0"
              data-testid={`button-move-up-${item.id}`}
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMoveDown(item.id)}
              disabled={!canMoveDown}
              className="h-8 w-8 p-0"
              data-testid={`button-move-down-${item.id}`}
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDuplicate(item.id)}
              className="h-8 w-8 p-0"
              data-testid={`button-duplicate-${item.id}`}
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(item.id)}
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              data-testid={`button-delete-${item.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

    </div>
  );
}

interface DraggableItemListProps {
  items: TemplateItem[];
  activeItemId: string | null;
  onItemsReorder: (newItems: TemplateItem[]) => void;
  onItemSelect: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onAddSubAnswer: (parentId: string) => void;
}

export function DraggableItemList({
  items,
  activeItemId,
  onItemsReorder,
  onItemSelect,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onAddSubAnswer,
}: DraggableItemListProps) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      onItemsReorder(arrayMove(items, oldIndex, newIndex));
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No items yet. Click "Add Item" to get started.</p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
          {items.map((item, index) => (
            <SortableItem
              key={item.id}
              item={item}
              index={index}
              totalItems={items.length}
              isActive={item.id === activeItemId}
              onSelect={onItemSelect}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onAddSubAnswer={onAddSubAnswer}
              onToggleExpanded={toggleExpanded}
              isExpanded={!expandedItems.has(item.id)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
